import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RuleIcon from '@mui/icons-material/Rule';
import { useSubsidiary } from '../../context/SubsidiaryContext';
import brandService from '../../services/brandService';

export default function SubsidiaryRulesManager() {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const { subsidiaries } = useSubsidiary();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rules, setRules] = useState([]);
  const [brands, setBrands] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    subsidiary_id: '',
    rule_name: '',
    currency_filter: '',
    geography_filter: '',
    brand_filter: [],
    connector_name_pattern: '',
    priority: 1,
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      // Load rules
      try {
        const rulesRes = await fetch('/api/subsidiaries/rules', {
          headers: { 'x-auth-token': token }
        });
        const rulesData = await rulesRes.json();
        if (rulesData?.success && rulesData?.data) {
          setRules(rulesData.data);
        }
      } catch (err) {
        console.error('Error loading rules:', err);
        setRules([]);
      }

      // Load brands
      try {
        const brandsRes = await brandService.getBrands();
        if (Array.isArray(brandsRes)) {
          setBrands(brandsRes);
        }
      } catch (err) {
        console.error('Error loading brands:', err);
        setBrands([]);
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load rules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        subsidiary_id: rule.subsidiary_id || '',
        rule_name: rule.rule_name || '',
        currency_filter: rule.currency_filter || '',
        geography_filter: rule.geography_filter || '',
        brand_filter: rule.brand_filter ? JSON.parse(rule.brand_filter) : [],
        connector_name_pattern: rule.connector_name_pattern || '',
        priority: rule.priority || 1,
        is_active: rule.is_active !== undefined ? rule.is_active : true
      });
    } else {
      setEditingRule(null);
      setFormData({
        subsidiary_id: '',
        rule_name: '',
        currency_filter: '',
        geography_filter: '',
        brand_filter: [],
        connector_name_pattern: '',
        priority: 1,
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('token');

    try {
      const url = editingRule
        ? `/api/subsidiaries/rules/${editingRule.id}`
        : '/api/subsidiaries/rules';

      const method = editingRule ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        handleCloseDialog();
        loadData();
      } else {
        alert('Error: ' + (data.message || 'Failed to save rule'));
      }
    } catch (err) {
      console.error('Error saving rule:', err);
      alert('Failed to save rule. Please try again.');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!await confirm({
      title: 'Delete Rule',
      message: 'Are you sure you want to delete this rule?',
      confirmText: 'Delete',
      severity: 'danger'
    })) {
      return;
    }

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`/api/subsidiaries/rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token }
      });

      const data = await response.json();

      if (data.success) {
        loadData();
      } else {
        alert('Error: ' + (data.message || 'Failed to delete rule'));
      }
    } catch (err) {
      console.error('Error deleting rule:', err);
      alert('Failed to delete rule. Please try again.');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RuleIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Assignment Rules ({rules.length})
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Rule
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Define rules to automatically assign connectors to subsidiaries based on currency, geography, brand, and name patterns.
            Rules are evaluated by priority (1 = highest).
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Rule Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subsidiary</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Filters</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No rules defined. Create your first rule to enable automatic connector assignment.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {rule.rule_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={rule.subsidiary_name} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {rule.currency_filter && (
                            <Chip label={`Currency: ${rule.currency_filter}`} size="small" />
                          )}
                          {rule.geography_filter && (
                            <Chip label={`Geo: ${rule.geography_filter}`} size="small" />
                          )}
                          {rule.connector_name_pattern && (
                            <Chip label={`Pattern: ${rule.connector_name_pattern}`} size="small" />
                          )}
                          {rule.brand_filter && rule.brand_filter !== '[]' && (
                            <Chip label={`${JSON.parse(rule.brand_filter).length} brands`} size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={rule.priority} size="small" color="secondary" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rule.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={rule.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(rule)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(rule.id)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Rule Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRule ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Rule Name"
              value={formData.rule_name}
              onChange={(e) => handleFormChange('rule_name', e.target.value)}
              required
              helperText="Descriptive name for this rule"
            />

            <FormControl fullWidth required>
              <InputLabel>Assign To Subsidiary</InputLabel>
              <Select
                value={formData.subsidiary_id}
                onChange={(e) => handleFormChange('subsidiary_id', e.target.value)}
                label="Assign To Subsidiary"
              >
                {subsidiaries.filter(s => !s.is_default).map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.name} ({sub.country}/{sub.currency})
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Connectors matching this rule will be assigned to this subsidiary</FormHelperText>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Currency Filter</InputLabel>
                <Select
                  value={formData.currency_filter}
                  onChange={(e) => handleFormChange('currency_filter', e.target.value)}
                  label="Currency Filter"
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Geography Filter</InputLabel>
                <Select
                  value={formData.geography_filter}
                  onChange={(e) => handleFormChange('geography_filter', e.target.value)}
                  label="Geography Filter"
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="US">US</MenuItem>
                  <MenuItem value="UK">UK</MenuItem>
                  <MenuItem value="AU">AU</MenuItem>
                  <MenuItem value="EU">EU</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Brand Filter</InputLabel>
              <Select
                multiple
                value={formData.brand_filter}
                onChange={(e) => handleFormChange('brand_filter', e.target.value)}
                input={<OutlinedInput label="Brand Filter" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const brand = brands.find(b => b.id === id);
                      return <Chip key={id} label={brand?.name || id} size="small" />;
                    })}
                  </Box>
                )}
              >
                {brands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Leave empty to match all brands</FormHelperText>
            </FormControl>

            <TextField
              fullWidth
              label="Connector Name Pattern"
              value={formData.connector_name_pattern}
              onChange={(e) => handleFormChange('connector_name_pattern', e.target.value)}
              helperText="Match connectors containing this text (case-insensitive)"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="number"
                label="Priority"
                value={formData.priority}
                onChange={(e) => handleFormChange('priority', parseInt(e.target.value))}
                InputProps={{ inputProps: { min: 1, max: 100 } }}
                helperText="Lower number = higher priority"
                sx={{ flex: 1 }}
              />

              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.is_active}
                  onChange={(e) => handleFormChange('is_active', e.target.value)}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.rule_name || !formData.subsidiary_id}
          >
            {editingRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
